import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PersonIcon from "@mui/icons-material/Person";
import ReplyIcon from "@mui/icons-material/Reply";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import XIcon from "@mui/icons-material/X";
import {
  Box,
  Button,
  Collapse,
  Container,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import viewpaths from "global/constants/ViewPathConstants";
import { handleErrorMessage } from "helpers/methods";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  boldFont,
  getRelativeFontSize,
  mediumFont,
  regularFont,
} from "utils/styles";
import fallbackImage from "../../../assets/images/thumb-panacea-insights.jpg";
import {
  selectAuthenticated,
  selectEmail,
  selectFirstName,
  selectLastName,
} from "../../../redux/authSlice";
import { Seo } from "components/seo";
import type { BlogDetailItem } from "../BlogsServices";
import {
  addBlogComment,
  getBlogById,
  getBlogComments,
  likeDislikeBlog,
} from "../BlogsServices";
import { formatDescriptionMarkdown } from "../formatDescriptionMarkdown";

const FALLBACK_IMAGE =
  typeof fallbackImage === "string"
    ? fallbackImage
    : ((fallbackImage as { default?: string })?.default ?? "");

const BlogDetail = () => {
  const theme = useTheme();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [blog, setBlog] = useState<BlogDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarBlogs, setSimilarBlogs] = useState<any[]>([]);
  const [topRecommendations, setTopRecommendations] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>();
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [replyName, setReplyName] = useState("");
  const [replyEmail, setReplyEmail] = useState("");
  const [formFields, setFormFields] = useState({
    name: "",
    email: "",
    website: "",
    comment: "",
  });
  const [commentsSectionExpanded, setCommentsSectionExpanded] = useState(false);
  const [replySectionExpanded, setReplySectionExpanded] = useState(false);
  const [isStarred, setIsStarred] = useState(false);
  const [starLoading, setStarLoading] = useState(false);

  const authenticated = useSelector(selectAuthenticated);
  const authUserName =
    useSelector(selectFirstName) + " " + useSelector(selectLastName);
  const authEmail = useSelector(selectEmail);

  // Auto-fill Name/Email when user is logged in; clear when logged out
  useEffect(() => {
    if (authenticated) {
      const name = authUserName?.trim() || authEmail?.split("@")[0] || "";
      setFormFields((prev) => ({
        ...prev,
        name,
        email: authEmail || "",
      }));
    } else {
      setFormFields((prev) => ({ ...prev, name: "", email: "" }));
    }
  }, [authenticated, authUserName, authEmail]);

  // Auto-fill Reply Name/Email when user is logged in and opens reply form
  useEffect(() => {
    if (replyingTo !== null && authenticated) {
      const name = authUserName?.trim() || authEmail?.split("@")[0] || "";
      setReplyName(name);
      setReplyEmail(authEmail || "");
    } else if (!authenticated && replyingTo !== null) {
      setReplyName("");
      setReplyEmail("");
    }
  }, [replyingTo, authenticated, authUserName, authEmail]);

  const fetchBlog = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBlogById(id);
      if (data) {
        setBlog({
          ...data,
          authorLink: data.authorLink ?? "#",
          subtitle: data.subtitle ?? "",
          quote: data.quote ?? "",
          content: data.content ?? "",
          isLiked: data.isLiked ?? false,
          likes: data.likes ?? 0,
        });
        const isLiked = data.likes > 0;
        setIsStarred(isLiked);
        await fetchComment(id);
        // const similar = mockSimilarBlogs.filter(
        //   (b) =>
        //     String(b.id) !== id &&
        //     (b.topic === data.topic || b.topic === data.topic?.toLowerCase())
        // );
        // setSimilarBlogs(
        //   similar.length > 0 ? similar : mockSimilarBlogs.slice(0, 3)
        // );
        // setTopRecommendations(mockTopRecommendations);
      }
      // else {
      //   setBlog({
      //     ...defaultContent,
      //     id: id,
      //     title: defaultContent.title,
      //     author: defaultContent.author,
      //     authorLink: defaultContent.authorLink,
      //     publishDate: defaultContent.publishDate,
      //     topic: defaultContent.topic,
      //     tags: defaultContent.tags,
      //     description: "",
      //     subtitle: defaultContent.subtitle,
      //     quote: defaultContent.quote,
      //     content: defaultContent.content,
      //     image: defaultContent.image,
      //   } as BlogDetailItem);
      //   setSimilarBlogs(mockSimilarBlogs.slice(0, 3));
      //   setTopRecommendations(mockTopRecommendations);
      // }
    } catch (err: any) {
      setError(err?.message ?? "Failed to load blog.");
    } finally {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [id]);
  console.log("blog", blog);
  console.log("isStarred", isStarred);

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  const fetchComment = async (id: string) => {
    try {
      const response = await getBlogComments(id);
      setComments(response.comments ?? []);
    } catch (err: any) {
      handleErrorMessage(err?.message ?? "Failed to load comments.");
    }
  };

  const handleSubmitComment = async () => {
    try {
      let comment: any = {
        author_name: formFields?.name ?? "",
        author_email: formFields?.email ?? "",
        website: formFields?.website ?? "",
        body: formFields?.comment ?? "",
      };
      setLoading(true);
      const response = await addBlogComment(id, comment);
      if (response.success) {
        fetchComment(id);
        setFormFields({
          name: "",
          email: "",
          website: "",
          comment: "",
        });
        setReplyName("");
        setReplyEmail("");
        setReplyContent("");
        setReplyingTo(null);
      }
    } catch (err: any) {
      handleErrorMessage(err?.message ?? "Failed to submit comment.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (parent_id: number) => {
    try {
      const comment: any = {
        parent_id,
        author_name: replyName ?? "",
        author_email: replyEmail ?? "",
        body: replyContent ?? "",
      };
      setLoading(true);
      const response = await addBlogComment(id, comment);
      if (response?.success) {
        fetchComment(id);
        setReplyName("");
        setReplyEmail("");
        setReplyContent("");
        setReplyingTo(null);
      }
    } catch (err: any) {
      handleErrorMessage(err?.message ?? "Failed to submit reply.");
    } finally {
      setLoading(false);
    }
  };

  const handleStarToggle = async () => {
    if (!id || starLoading) return;
    const newStarred = !isStarred;
    setStarLoading(true);
    try {
      await likeDislikeBlog(id, newStarred);
      setIsStarred(newStarred);
    } catch (err: any) {
      handleErrorMessage(err?.message ?? "Failed to update star.");
    } finally {
      setStarLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading && !blog) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          pt: { xs: 10, md: 12 },
          pb: 8,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <Skeleton
              variant="rectangular"
              height={320}
              sx={{
                borderRadius: 2,
                bgcolor:
                  theme.palette.mode === "dark" ? "grey.800" : "grey.200",
              }}
              animation="wave"
            />
            <Box sx={{ pt: 2 }}>
              <Skeleton
                variant="text"
                width="85%"
                height={48}
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width="60%"
                height={48}
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                }}
                animation="wave"
              />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Skeleton
                  variant="text"
                  width={120}
                  height={28}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                  }}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width={100}
                  height={28}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                  }}
                  animation="wave"
                />
              </Stack>
            </Box>
            <Box sx={{ pt: 4 }}>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  width={i === 3 ? "70%" : "100%"}
                  height={24}
                  sx={{
                    mt: 1.5,
                    bgcolor:
                      theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                  }}
                  animation="wave"
                />
              ))}
            </Box>
          </Stack>
        </Container>
      </Box>
    );
  }

  if (!blog) {
    return null;
  }

  return (
    <>
      <Seo
        title={blog?.seoTitle || blog?.title}
        description={blog?.metaDescription || blog?.description}
        image={blog?.image || undefined}
        url={window.location.href}
        type="article"
      />
      <style>
        {`
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 145% 50%;
            }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 145% 50%;
            }
          }
          @keyframes shimmer {
            0% {
              background-position: -1000px 0;
            }
            100% {
              background-position: 1000px 0;
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0) translateX(0);
            }
            50% {
              transform: translateY(-20px) translateX(10px);
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 0.6;
              transform: scale(1);
            }
            50% {
              opacity: 1;
              transform: scale(1.05);
            }
          }
          .blog-content {
            line-height: 1.9;
            color: ${theme.palette.text.primary};
          }
          .blog-content .blog-content-h1,
          .blog-content .blog-markdown-h1,
          .blog-content h1 {
            font-size: 1.75rem;
            font-weight: 700;
            margin: 0px;
            color: ${theme.palette.text.primary};
            line-height: 1.3;
          }
          .blog-content .blog-content-h2,
          .blog-content .blog-markdown-h2,
          .blog-content h2 {
            font-size: 1.4rem;
            font-weight: 700;
            margin: 0px;
            color: ${theme.palette.text.primary};
            line-height: 1.3;
          }
          .blog-content .blog-content-h3,
          .blog-content .blog-markdown-h3,
          .blog-content h3 {
            font-size: 1.2rem;
            font-weight: 700;
            margin: 0px;
            color: ${theme.palette.text.primary};
            line-height: 1.3;
          }
          .blog-content .blog-markdown-h1,
          .blog-content .blog-markdown-h2,
          .blog-content .blog-markdown-h4,
          .blog-content .blog-markdown-h5,
          .blog-content .blog-markdown-h6,
          .blog-content h4,
          .blog-content h5,
          .blog-content h6 {
            font-weight: 700;
            margin: 0px;
            color: ${theme.palette.text.primary};
            line-height: 1.3;
          }
          .blog-content h4, .blog-content .blog-markdown-h4 { font-size: 1.1rem; }
          .blog-content h5, .blog-content .blog-markdown-h5 { font-size: 1rem; }
          .blog-content h6, .blog-content .blog-markdown-h6 { font-size: 0.95rem; }
          .blog-content .blog-content-p,
          .blog-content .blog-markdown-p,
          .blog-content p {
            margin: 0px;
            font-size: 17px;
            line-height: 1.9;
            color: ${theme.palette.text.secondary};
          }
          .blog-content .blog-content-p:first-of-type,
          .blog-content p:first-of-type {
            font-size: 19px;
            font-weight: 400;
            color: ${theme.palette.text.primary};
            line-height: 1.9;
          }
          .blog-content .blog-content-blockquote,
          .blog-content .blog-markdown-blockquote,
          .blog-content blockquote {
            margin: 0px;
            padding: 16px 20px;
            border-left: 4px solid #f8481c;
            background: ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.04)"
            };
            border-radius: 0 8px 8px 0;
            font-style: italic;
          }
          .blog-content .blog-content-hr,
          .blog-content .blog-markdown-hr,
          .blog-content hr {
            border: none;
            border-top: 1px solid ${
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.5)
                : alpha(theme.palette.common.black, 0.45)
            };
            margin: 0px;
          }
          .blog-content .blog-content-ul,
          .blog-content .blog-content-ol,
          .blog-content .blog-markdown-ul,
          .blog-content .blog-markdown-ol,
          .blog-content ul,
          .blog-content ol {
            margin: 0px;
            padding-left: 1.5rem;
          }
          .blog-content .blog-content-ul li,
          .blog-content .blog-content-ol li,
          .blog-content .blog-markdown-ul li,
          .blog-content .blog-markdown-ol li,
          .blog-content ul li,
          .blog-content ol li {
            margin: 0px;
            line-height: 1.7;
            color: ${theme.palette.text.secondary};
          }
          .blog-content .blog-content-table,
          .blog-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 15px;
          }
          .blog-content .blog-content-table th,
          .blog-content .blog-content-table td,
          .blog-content table th,
          .blog-content table td {
            border: 1px solid ${theme.palette.divider};
            padding: 10px 14px;
            text-align: left;
          }
          .blog-content .blog-content-table th,
          .blog-content table th {
            font-weight: 700;
            background: ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "rgba(0,0,0,0.04)"
            };
          }
          .blog-content code,
          .blog-content .blog-markdown-code {
            background: ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.06)"
            };
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 0.9em;
          }
          .blog-content .blog-markdown-link {
            color: #f8481c;
            text-decoration: underline;
          }
          .blog-content .blog-markdown-link:hover {
            text-decoration: none;
          }
          .blog-content .blog-markdown-img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            margin: 12px 0;
          }
          .blog-content .panacea-takeaway-header {
            position: relative;
            color: ${theme.palette.mode === "dark" ? "#ff6b4a" : "#c0392b"};
            font-weight: 800;
            font-size: 1.35rem;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            padding: 16px 20px 16px 24px;
            margin: 0px;
            border: none;
            border-left: 5px solid #f8481c;
            border-radius: 0 12px 12px 0;
            background: linear-gradient(
              105deg,
              ${alpha("#f8481c", theme.palette.mode === "dark" ? 0.22 : 0.12)} 0%,
              ${alpha("#ff6b4a", theme.palette.mode === "dark" ? 0.08 : 0.05)} 50%,
              ${alpha(theme.palette.background.paper, 0.5)} 100%
            );
            box-shadow: 0 4px 20px ${alpha("#f8481c", theme.palette.mode === "dark" ? 0.2 : 0.12)};
          }
          .blog-content .panacea-takeaway-header::before {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            width: 60px;
            height: 100%;
            background: linear-gradient(90deg, transparent 0%, ${alpha("#f8481c", 0.06)} 100%);
            border-radius: 0 12px 12px 0;
            pointer-events: none;
          }
          .blog-content .panacea-takeaway-header::after {
            content: "◆";
            position: absolute;
            left: 8px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 0.5rem;
            color: #f8481c;
            opacity: 0.8;
          }
          .blog-content .panacea-takeaway-header {
            padding-left: 28px;
          }
        `}
      </style>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Refined Background Effects */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {/* Elegant Gradient Mesh */}
          <Box
            sx={{
              position: "absolute",
              top: "-25%",
              left: "-25%",
              right: "-25%",
              bottom: "-25%",
              width: "150%",
              height: "150%",
              background:
                theme.palette.mode === "dark"
                  ? "radial-gradient(ellipse at 20% 30%, rgba(255, 255, 255, 0.06) 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(248, 72, 28, 0.08) 0%, transparent 55%), radial-gradient(ellipse at 50% 50%, rgba(200, 200, 200, 0.04) 0%, transparent 55%)"
                  : "radial-gradient(ellipse at 20% 30%, rgba(0, 0, 0, 0.03) 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(248, 72, 28, 0.05) 0%, transparent 55%), radial-gradient(ellipse at 50% 50%, rgba(100, 100, 100, 0.02) 0%, transparent 55%)",
              filter: "blur(100px)",
              opacity: 0.7,
            }}
          />

          {/* Subtle Grid Pattern */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                theme.palette.mode === "dark"
                  ? `linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)`
                  : `linear-gradient(rgba(0, 0, 0, 0.015) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(0, 0, 0, 0.015) 1px, transparent 1px)`,
              backgroundSize: "100px 100px",
              maskImage:
                "radial-gradient(ellipse at center, black 25%, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at center, black 25%, transparent 75%)",
              opacity: 0.4,
            }}
          />

          {/* Refined Gradient Orbs - Only 2 for cleaner look */}
          <Box
            sx={{
              position: "absolute",
              top: "5%",
              right: "15%",
              width: "500px",
              height: "500px",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(
                "#f8481c",
                theme.palette.mode === "dark" ? 0.08 : 0.04,
              )} 0%, transparent 65%)`,
              filter: "blur(80px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "10%",
              left: "10%",
              width: "400px",
              height: "400px",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(
                "#f8481c",
                theme.palette.mode === "dark" ? 0.06 : 0.03,
              )} 0%, transparent 65%)`,
              filter: "blur(70px)",
            }}
          />
        </Box>

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          {/* Sticky share bar - fixed to viewport, compact */}
          {blog && (
            <Stack
              direction="column"
              spacing={0.5}
              sx={{
                position: "fixed",
                right: { xs: 8, sm: 16 },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 20,
                py: 1,
                px: 0.5,
                borderRadius: "12px",
                background: alpha(theme.palette.background.paper, 0.92),
                border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                backdropFilter: "blur(12px)",
              }}
            >
              {/* Star for recommendations – logged-in only, visually distinct from share icons */}
              {authenticated && (
                <Box
                  sx={{
                    pb: 0.75,
                    mb: 0.5,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                  }}
                >
                  <IconButton
                    size="small"
                    disabled={starLoading}
                    onClick={handleStarToggle}
                    aria-label={isStarred ? "Unstar blog" : "Star blog"}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      color: isStarred ? "#fff" : theme.palette.text.secondary,
                      bgcolor: isStarred
                        ? "#f8481c"
                        : alpha(theme.palette.divider, 0.15),
                      border: `2px solid ${
                        isStarred ? "#f8481c" : "transparent"
                      }`,
                      "&:hover": {
                        bgcolor: isStarred
                          ? alpha("#f8481c", 0.85)
                          : alpha("#f8481c", 0.12),
                        color: isStarred ? "#fff" : "#f8481c",
                        borderColor: "#f8481c",
                      },
                    }}
                  >
                    {isStarred ? (
                      <StarIcon sx={{ fontSize: 22 }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 22 }} />
                    )}
                  </IconButton>
                </Box>
              )}
              <IconButton
                size="small"
                onClick={() =>
                  window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`,
                    "_blank",
                  )
                }
                sx={{
                  color: theme.palette.text.secondary,
                  width: 40,
                  height: 40,
                  "&:hover": {
                    color: "#1877F2",
                    bgcolor: alpha("#1877F2", 0.1),
                  },
                }}
              >
                <FacebookIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  window.open(
                    `https://twitter.com/intent/tweet?url=${window.location.href}&text=${encodeURIComponent(blog.title)}`,
                    "_blank",
                  )
                }
                sx={{
                  color: theme.palette.text.secondary,
                  width: 40,
                  height: 40,
                  "&:hover": { color: "#000", bgcolor: alpha("#fff", 0.08) },
                }}
              >
                <XIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  const text = `${blog.title} ${window.location.href}`;
                  navigator.clipboard.writeText(text);
                  alert(
                    "Blog link copied! You can paste it in your Instagram story.",
                  );
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  width: 40,
                  height: 40,
                  "&:hover": {
                    color: "#E4405F",
                    bgcolor: alpha("#E4405F", 0.1),
                  },
                }}
              >
                <InstagramIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  window.open(
                    `https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`,
                    "_blank",
                  )
                }
                sx={{
                  color: theme.palette.text.secondary,
                  width: 40,
                  height: 40,
                  "&:hover": {
                    color: "#0077B5",
                    bgcolor: alpha("#0077B5", 0.1),
                  },
                }}
              >
                <LinkedInIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  (window.location.href = `mailto:?subject=${encodeURIComponent(blog.title)}&body=${encodeURIComponent(window.location.href)}`)
                }
                sx={{
                  color: theme.palette.text.secondary,
                  width: 40,
                  height: 40,
                  "&:hover": {
                    color: "#f8481c",
                    bgcolor: alpha("#f8481c", 0.1),
                  },
                }}
              >
                <EmailIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert("Blog link copied to clipboard!");
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  width: 40,
                  height: 40,
                  "&:hover": {
                    color: "#f8481c",
                    bgcolor: alpha("#f8481c", 0.1),
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Stack>
          )}

          {/* Back to Blogs - clear spacing below navbar, styled as link button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              sx={{
                pt: { xs: 12, md: 13 },
                pb: 3,
                px: { xs: 0, sm: 0 },
              }}
            >
              <Box
                component="button"
                onClick={() => history.push(viewpaths.blogsViewPath)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 1.25,
                  border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                  borderRadius: "10px",
                  backgroundColor: alpha(theme.palette.background.paper, 0.85),
                  backdropFilter: "blur(12px)",
                  color: theme.palette.text.secondary,
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  boxShadow: `0 2px 8px ${alpha(
                    theme.palette.common.black,
                    0.06,
                  )}`,
                  "&:hover": {
                    backgroundColor: alpha("#f8481c", 0.08),
                    borderColor: alpha("#f8481c", 0.4),
                    color: "#f8481c",
                    boxShadow: `0 4px 12px ${alpha("#f8481c", 0.12)}`,
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: "20px" }} />
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  Back to Blogs
                </Typography>
              </Box>
            </Box>
          </motion.div>

          {error && (
            <Box
              sx={{
                mx: 2,
                mb: 2,
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
              }}
            >
              <Typography
                sx={{ color: theme.palette.text.secondary, fontSize: "14px" }}
              >
                {error}
              </Typography>
              <Button
                size="small"
                onClick={() => fetchBlog()}
                sx={{
                  color: "#f8481c",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Retry
              </Button>
            </Box>
          )}

          {/* Main Content Area */}
          <Grid container spacing={{ xs: 3, md: 12 }}>
            {/* Left Column - Main Content */}
            <Grid item xs={12} md={12}>
              {/* Title Section - FIRST - Cleaner Design */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <Box
                  sx={{
                    mb: { xs: 5, md: 7 },
                    position: "relative",
                  }}
                >
                  {/* Title with Elegant Gradient */}
                  <Typography
                    sx={{
                      ...boldFont,
                      fontSize: {
                        xs: getRelativeFontSize(14),
                        md: getRelativeFontSize(20),
                        lg: getRelativeFontSize(24),
                      },
                      lineHeight: 1.15,
                      mb: 4,
                      letterSpacing: "-0.02em",
                      background:
                        theme.palette.mode === "dark"
                          ? "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 70%, #f8481c 90%, #ff6b4a 100%)"
                          : "linear-gradient(135deg, #000000 0%, #000000 70%, #f8481c 90%, #ff6b4a 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundSize: "300% 300%",
                      animation: "gradientShift 6s ease infinite",
                      position: "relative",
                      pb: 4,
                      borderBottom: `3px solid ${alpha("#f8481c", 0.2)}`,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatDescriptionMarkdown(blog.title ?? ""),
                    }}
                  />

                  {/* Meta Information - Cleaner Layout */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 2, sm: 3 }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexWrap="wrap"
                    sx={{
                      pt: 3,
                    }}
                  >
                    {/* Date */}
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CalendarTodayIcon
                        sx={{
                          fontSize: "20px",
                          color: "#f8481c",
                        }}
                      />
                      <Typography
                        sx={{
                          ...mediumFont,
                          fontSize: getRelativeFontSize(1),
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {formatDate(blog.publishDate)}
                      </Typography>
                    </Stack>

                    {/* Divider */}
                    <Box
                      sx={{
                        width: "1px",
                        height: "24px",
                        backgroundColor: alpha(theme.palette.divider, 0.3),
                        display: { xs: "none", sm: "block" },
                      }}
                    />

                    {/* Author */}
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <PersonIcon
                        sx={{
                          fontSize: "20px",
                          color: "#f8481c",
                        }}
                      />
                      <Typography
                        sx={{
                          ...mediumFont,
                          fontSize: getRelativeFontSize(1),
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {blog.author}
                      </Typography>
                    </Stack>

                    {/* Divider */}
                    <Box
                      sx={{
                        width: "1px",
                        height: "24px",
                        backgroundColor: alpha(theme.palette.divider, 0.3),
                        display: { xs: "none", sm: "block" },
                      }}
                    />

                    {/* Category */}
                    <Box
                      sx={{
                        ...boldFont,
                        fontSize: getRelativeFontSize(2),
                        color: "#f8481c",
                        px: 2.5,
                        py: 1,
                        borderRadius: "8px",
                        backgroundColor: alpha("#f8481c", 0.1),
                        border: `2px solid ${alpha("#f8481c", 0.3)}`,
                      }}
                    >
                      {blog.topic}
                    </Box>

                    {/* Tags */}
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      alignItems="center"
                    >
                      {blog.tags.map((tag: string) => (
                        <Box
                          key={tag}
                          sx={{
                            ...regularFont,
                            fontSize: getRelativeFontSize(0),
                            color: theme.palette.text.secondary,
                            px: 2,
                            py: 0.75,
                            borderRadius: "6px",
                            backgroundColor: alpha(
                              theme.palette.background.paper,
                              0.8,
                            ),
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2,
                            )}`,
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </motion.div>

              {/* Image Section - SECOND - Creative Design */}
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: "400px", md: "750px" },
                    borderRadius: "4px",
                    overflow: "hidden",
                    mb: { xs: 6, md: 8 },
                    boxShadow: `0 32px 100px ${alpha(
                      theme.palette.mode === "dark" ? "#000000" : "#000000",
                      0.3,
                    )}, 0 0 0 1px ${alpha(theme.palette.divider, 0.1)}`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(180deg, ${alpha(
                        theme.palette.background.default,
                        0.1,
                      )} 0%, transparent 50%, ${alpha(
                        theme.palette.background.default,
                        0.15,
                      )} 100%)`,
                      zIndex: 1,
                      pointerEvents: "none",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: "-2px",
                      left: "-2px",
                      right: "-2px",
                      bottom: "-2px",
                      background: `linear-gradient(135deg, ${alpha(
                        "#f8481c",
                        0.3,
                      )} 0%, ${alpha("#ff6b4a", 0.2)} 50%, transparent 100%)`,
                      borderRadius: "12px",
                      zIndex: -1,
                      filter: "blur(20px)",
                      opacity: 0.6,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={blog.image}
                    alt={blog.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target && FALLBACK_IMAGE) target.src = FALLBACK_IMAGE;
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      position: "relative",
                      zIndex: 0,
                      transition: "transform 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "scale(1.08)",
                      },
                    }}
                  />
                  {/* Creative Corner Accents */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "150px",
                      height: "150px",
                      background: `linear-gradient(135deg, ${alpha(
                        "#f8481c",
                        0.7,
                      )} 0%, transparent 70%)`,
                      zIndex: 2,
                      pointerEvents: "none",
                      clipPath: "polygon(0 0, 100% 0, 0 100%)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: "120px",
                      height: "120px",
                      background: `linear-gradient(315deg, ${alpha(
                        "#ff6b4a",
                        0.5,
                      )} 0%, transparent 70%)`,
                      zIndex: 2,
                      pointerEvents: "none",
                      clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
                    }}
                  />
                </Box>
              </motion.div>

              {/* Subtitle/Heading - Creative Design */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    mb: 6,
                    pl: { xs: 5, md: 6 },
                    pr: { xs: 3, md: 4 },
                    py: 3,
                    background: `linear-gradient(90deg, ${alpha(
                      "#f8481c",
                      0.1,
                    )} 0%, ${alpha("#f8481c", 0.03)} 50%, transparent 100%)`,
                    borderRadius: "0 16px 16px 0",
                    borderLeft: `5px solid ${alpha("#f8481c", 0.9)}`,
                    boxShadow: `0 4px 20px ${alpha(
                      theme.palette.mode === "dark" ? "#000000" : "#000000",
                      0.08,
                    )}`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: "-2.5px",
                      top: 0,
                      bottom: 0,
                      width: "5px",
                      background:
                        "linear-gradient(180deg, #f8481c 0%, #ff6b4a 100%)",
                      borderRadius: "3px",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "80px",
                      height: "100%",
                      background: `linear-gradient(90deg, transparent 0%, ${alpha(
                        "#f8481c",
                        0.05,
                      )} 100%)`,
                      borderRadius: "0 16px 16px 0",
                    },
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      ...boldFont,
                      fontSize: {
                        xs: getRelativeFontSize(10),
                        md: getRelativeFontSize(14),
                        lg: getRelativeFontSize(16),
                      },
                      lineHeight: 1.4,
                      color: theme.palette.text.primary,
                      letterSpacing: "-0.01em",
                      position: "relative",
                      zIndex: 1,
                      "& p": { margin: 0 },
                      "& strong": { fontWeight: 700 },
                      "& em": { fontStyle: "italic" },
                      "& a": {
                        color: "#f8481c",
                        textDecoration: "underline",
                      },
                      "& code": {
                        fontSize: "0.9em",
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.divider, 0.3),
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatDescriptionMarkdown(blog.subtitle ?? ""),
                    }}
                  />
                </Box>
              </motion.div>

              {/* Blog Content - Creative Design */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    mb: 7,
                    borderRadius: "12px",
                  }}
                >
                  <Box
                    className="blog-content"
                    sx={{
                      maxWidth: "100%",
                      p: { xs: 4, md: 5 },
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.background.paper,
                        0.6,
                      )} 0%, ${alpha(
                        theme.palette.background.default,
                        0.9,
                      )} 100%)`,
                      backdropFilter: "blur(20px)",
                      borderRadius: "12px",
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: `0 8px 32px ${alpha(
                        theme.palette.mode === "dark" ? "#000000" : "#000000",
                        0.1,
                      )}`,
                      position: "relative",
                      // TODO: Re-enable login restriction later
                      // ...(!authenticated && {
                      //   maxHeight: "72vh",
                      //   overflow: "hidden",
                      // }),
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "4px",
                        height: "100%",
                        background: `linear-gradient(180deg, ${alpha(
                          "#f8481c",
                          0.7,
                        )} 0%, ${alpha("#ff6b4a", 0.5)} 100%)`,
                        borderRadius: "12px 0 0 12px",
                      },
                      "& p": {
                        ...regularFont,
                        fontSize: {
                          xs: getRelativeFontSize(2),
                          md: getRelativeFontSize(3),
                        },
                        lineHeight: 1.9,
                        color: theme.palette.text.secondary,
                        mb: 3,
                      },
                      "& p:first-of-type": {
                        ...mediumFont,
                        fontSize: {
                          xs: getRelativeFontSize(2),
                          md: getRelativeFontSize(3),
                        },
                        color: theme.palette.text.primary,
                        lineHeight: 1.85,
                      },
                      "& h3": {
                        ...boldFont,
                        fontSize: {
                          xs: getRelativeFontSize(10),
                          md: getRelativeFontSize(12),
                        },
                        color: theme.palette.text.primary,
                        mt: 6,
                        mb: 3,
                        lineHeight: 1.4,
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatDescriptionMarkdown(blog.content ?? ""),
                    }}
                  />
                  {/* TODO: Re-enable login restriction later
                  {!authenticated && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "42%",
                        minHeight: 200,
                        background: `linear-gradient(to top, ${theme.palette.background.paper} 0%, ${alpha(
                          theme.palette.background.paper,
                          0.97,
                        )} 25%, ${alpha(
                          theme.palette.background.paper,
                          0.7,
                        )} 55%, transparent)`,
                        borderRadius: "0 0 12px 12px",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        pb: 3,
                        pt: 8,
                        px: 2,
                      }}
                    >
                      <Box
                        component={motion.div}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        sx={{
                          width: "100%",
                          maxWidth: 440,
                          mx: "auto",
                          p: 3,
                          borderRadius: "4px",
                          border: `1px solid ${alpha(
                            theme.palette.divider,
                            0.15,
                          )}`,
                          boxShadow: `0 24px 48px ${alpha(
                            theme.palette.common.black,
                            theme.palette.mode === "dark" ? 0.4 : 0.12,
                          )}, 0 0 0 1px ${alpha(
                            theme.palette.primary.main,
                            0.08,
                          )}`,
                          background:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.background.paper, 0.92)
                              : alpha(theme.palette.background.paper, 0.98),
                          backdropFilter: "blur(16px)",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: `linear-gradient(90deg, #f8481c, #ff6b4a)`,
                            borderRadius: "16px 16px 0 0",
                          },
                        }}
                      >
                        <Stack
                          alignItems="center"
                          spacing={2}
                          sx={{ textAlign: "center" }}
                        >
                          <Box
                            sx={{
                              width: 52,
                              height: 52,
                              borderRadius: "50%",
                              background: `linear-gradient(135deg, ${alpha(
                                "#f8481c",
                                0.15,
                              )} 0%, ${alpha("#ff6b4a", 0.1)} 100%)`,
                              border: `2px solid ${alpha("#f8481c", 0.35)}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <MenuBookIcon
                              sx={{
                                fontSize: 28,
                                color: "#f8481c",
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              ...mediumFont,
                              fontWeight: 600,
                              fontSize: getRelativeFontSize(4),
                              color: theme.palette.text.primary,
                              lineHeight: 1.3,
                            }}
                          >
                            Log in to see the rest of this article
                          </Typography>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              history.push(viewpaths.loginViewPath, {
                                redirectTo: `/blog/${id}`,
                              })
                            }
                            sx={{
                              ...mediumFont,
                              fontSize: getRelativeFontSize(0),
                              py: 1.5,
                              borderRadius: "12px",
                              textTransform: "none",
                              background: `linear-gradient(135deg, #f8481c 0%, #ff6b4a 100%)`,
                              boxShadow: `0 6px 20px ${alpha("#f8481c", 0.35)}`,
                              "&:hover": {
                                background: `linear-gradient(135deg, #e03d14 0%, #f85a38 100%)`,
                                boxShadow: `0 8px 24px ${alpha("#f8481c", 0.45)}`,
                              },
                            }}
                          >
                            Login
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  )}
                  */}
                </Box>
              </motion.div>

              {/* Quote Block - Creative Design */}
              {blog.quote && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      pl: { xs: 5, md: 7 },
                      pr: { xs: 3, md: 4 },
                      py: 5,
                      mb: 7,
                      borderLeft: `4px solid ${alpha("#f8481c", 0.8)}`,
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.background.paper,
                        0.8,
                      )} 0%, ${alpha("#f8481c", 0.05)} 100%)`,
                      backdropFilter: "blur(20px)",
                      borderRadius: "0 12px 12px 0",
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: `0 12px 40px ${alpha(
                        theme.palette.mode === "dark" ? "#000000" : "#000000",
                        0.12,
                      )}`,
                      "&::before": {
                        content: '"\\201C"',
                        position: "absolute",
                        top: "15px",
                        left: { xs: "20px", md: "28px" },
                        fontSize: { xs: "90px", md: "120px" },
                        color: alpha("#f8481c", 0.12),
                        fontFamily: "Georgia, serif",
                        lineHeight: 1,
                        fontWeight: 400,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        width: "60px",
                        height: "60px",
                        background: `linear-gradient(135deg, ${alpha(
                          "#f8481c",
                          0.1,
                        )} 0%, transparent 100%)`,
                        borderRadius: "0 0 12px 0",
                      },
                    }}
                  >
                    <Box
                      component="div"
                      sx={{
                        ...mediumFont,
                        fontSize: {
                          xs: getRelativeFontSize(3),
                          md: getRelativeFontSize(4),
                        },
                        lineHeight: 1.8,
                        color: theme.palette.text.primary,
                        fontStyle: "italic",
                        position: "relative",
                        zIndex: 1,
                        "& p": { margin: 0 },
                        "& strong": { fontWeight: 700 },
                        "& em": { fontStyle: "italic" },
                        "& a": {
                          color: "#f8481c",
                          textDecoration: "underline",
                        },
                        "& code": {
                          fontSize: "0.9em",
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.divider, 0.3),
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatDescriptionMarkdown(blog.quote ?? ""),
                      }}
                    />
                  </Box>
                </motion.div>
              )}

              {/* Comments Display Section - Collapsible */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <Box
                  sx={{
                    mt: 6,
                    pt: 5,
                    borderTop: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "-2px",
                      left: 0,
                      width: "100px",
                      height: "2px",
                      background:
                        "linear-gradient(90deg, #f8481c 0%, transparent 100%)",
                    },
                  }}
                >
                  <Box
                    onClick={() => setCommentsSectionExpanded((prev) => !prev)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      py: 1.5,
                      px: 2,
                      borderRadius: "10px",
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      background: commentsSectionExpanded
                        ? alpha(
                            "#f8481c",
                            theme.palette.mode === "dark" ? 0.15 : 0.08,
                          )
                        : alpha(theme.palette.background.paper, 0.9),
                      "&:hover": {
                        background: alpha(
                          "#f8481c",
                          theme.palette.mode === "dark" ? 0.2 : 0.12,
                        ),
                        borderColor: alpha("#f8481c", 0.4),
                      },
                      transition:
                        "background 0.2s ease, border-color 0.2s ease",
                    }}
                  >
                    <ChatBubbleOutlineIcon
                      sx={{ fontSize: "28px", color: "#f8481c", flexShrink: 0 }}
                    />
                    <Typography
                      component="h3"
                      sx={{
                        ...boldFont,
                        flex: 1,
                        fontSize: {
                          xs: getRelativeFontSize(12),
                          md: getRelativeFontSize(15),
                        },
                        color: theme.palette.text.primary,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Comments ({comments?.length ?? 0})
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: "28px",
                        color: "#f8481c",
                        transform: commentsSectionExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.25s ease",
                      }}
                    />
                  </Box>

                  <Collapse in={commentsSectionExpanded}>
                    <Box sx={{ pt: 3 }}>
                      {/* Comments List */}
                      {comments && comments.length > 0 && (
                        <Stack spacing={4}>
                          {comments.map((comment) => (
                            <Box key={comment.id}>
                              {/* Main Comment */}
                              <Box
                                sx={{
                                  p: 3,
                                  background: `linear-gradient(135deg, ${alpha(
                                    theme.palette.background.paper,
                                    0.6,
                                  )} 0%, ${alpha(
                                    theme.palette.background.default,
                                    0.8,
                                  )} 100%)`,
                                  backdropFilter: "blur(10px)",
                                  borderRadius: "12px",
                                  border: `1px solid ${alpha(
                                    theme.palette.divider,
                                    0.1,
                                  )}`,
                                  boxShadow: `0 4px 16px ${alpha(
                                    theme.palette.mode === "dark"
                                      ? "#000000"
                                      : "#000000",
                                    0.06,
                                  )}`,
                                  position: "relative",
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "4px",
                                    height: "100%",
                                    background: `linear-gradient(180deg, ${alpha(
                                      "#f8481c",
                                      0.6,
                                    )} 0%, ${alpha("#ff6b4a", 0.4)} 100%)`,
                                    borderRadius: "12px 0 0 12px",
                                  },
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="flex-start"
                                >
                                  <Box
                                    sx={{
                                      width: "48px",
                                      height: "48px",
                                      borderRadius: "50%",
                                      background: `linear-gradient(135deg, ${alpha(
                                        "#f8481c",
                                        0.2,
                                      )} 0%, ${alpha("#ff6b4a", 0.15)} 100%)`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      border: `2px solid ${alpha("#f8481c", 0.3)}`,
                                    }}
                                  >
                                    <PersonIcon
                                      sx={{
                                        color: "#f8481c",
                                        fontSize: "24px",
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Stack
                                      direction="row"
                                      spacing={2}
                                      alignItems="center"
                                      mb={1}
                                    >
                                      <Typography
                                        sx={{
                                          ...boldFont,
                                          fontSize: getRelativeFontSize(1),
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {comment.author_name}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          ...regularFont,
                                          fontSize: getRelativeFontSize(0),
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        {formatDate(comment.created_at)}
                                      </Typography>
                                    </Stack>
                                    <Typography
                                      sx={{
                                        ...regularFont,
                                        fontSize: getRelativeFontSize(1),
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.7,
                                        mb: 2,
                                      }}
                                    >
                                      {comment.body}
                                    </Typography>
                                    <Button
                                      startIcon={<ReplyIcon />}
                                      onClick={() =>
                                        setReplyingTo(
                                          replyingTo === comment.id
                                            ? null
                                            : comment.id,
                                        )
                                      }
                                      sx={{
                                        ...mediumFont,
                                        fontSize: getRelativeFontSize(0),
                                        color: "#f8481c",
                                        textTransform: "none",
                                        px: 2,
                                        py: 0.5,
                                        minWidth: "auto",
                                        "&:hover": {
                                          backgroundColor: alpha(
                                            "#f8481c",
                                            0.1,
                                          ),
                                        },
                                      }}
                                    >
                                      Reply
                                    </Button>
                                  </Box>
                                </Stack>
                              </Box>

                              {/* Reply Form */}
                              {replyingTo === comment.id && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    ml: { xs: 0, md: 8 },
                                    p: 3,
                                    background: alpha(
                                      theme.palette.background.paper,
                                      0.5,
                                    ),
                                    backdropFilter: "blur(10px)",
                                    borderRadius: "8px",
                                    border: `1px solid ${alpha(
                                      theme.palette.divider,
                                      0.1,
                                    )}`,
                                  }}
                                >
                                  <Stack spacing={2} sx={{ width: "100%" }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 2,
                                        flexWrap: "wrap",
                                        width: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          flex: "1 1 0",
                                          minWidth: { xs: "100%", sm: 0 },
                                        }}
                                      >
                                        <TextField
                                          placeholder="Your Name *"
                                          fullWidth
                                          size="small"
                                          disabled={authenticated}
                                          value={replyName}
                                          onChange={(e) =>
                                            setReplyName(e.target.value)
                                          }
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              ...regularFont,
                                              fontSize: getRelativeFontSize(0),
                                              backgroundColor: alpha(
                                                theme.palette.background
                                                  .default,
                                                0.8,
                                              ),
                                              "& fieldset": {
                                                borderColor: alpha(
                                                  theme.palette.divider,
                                                  0.2,
                                                ),
                                              },
                                              "&:hover fieldset": {
                                                borderColor: alpha(
                                                  "#f8481c",
                                                  0.4,
                                                ),
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#f8481c",
                                              },
                                            },
                                            "& .MuiInputBase-input": {
                                              ...regularFont,
                                              fontSize: getRelativeFontSize(0),
                                              "&::placeholder": {
                                                color:
                                                  theme.palette.text.secondary,
                                                opacity: 0.7,
                                                ...regularFont,
                                                fontSize:
                                                  getRelativeFontSize(0),
                                              },
                                            },
                                          }}
                                        />
                                      </Box>
                                      <Box
                                        sx={{
                                          flex: "1 1 0",
                                          minWidth: { xs: "100%", sm: 0 },
                                        }}
                                      >
                                        <TextField
                                          type="email"
                                          placeholder="Your Email *"
                                          fullWidth
                                          size="small"
                                          disabled={authenticated}
                                          value={replyEmail}
                                          onChange={(e) =>
                                            setReplyEmail(e.target.value)
                                          }
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              ...regularFont,
                                              fontSize: getRelativeFontSize(0),
                                              backgroundColor: alpha(
                                                theme.palette.background
                                                  .default,
                                                0.8,
                                              ),
                                              "& fieldset": {
                                                borderColor: alpha(
                                                  theme.palette.divider,
                                                  0.2,
                                                ),
                                              },
                                              "&:hover fieldset": {
                                                borderColor: alpha(
                                                  "#f8481c",
                                                  0.4,
                                                ),
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#f8481c",
                                              },
                                            },
                                            "& .MuiInputBase-input": {
                                              ...regularFont,
                                              fontSize: getRelativeFontSize(0),
                                              "&::placeholder": {
                                                color:
                                                  theme.palette.text.secondary,
                                                opacity: 0.7,
                                                ...regularFont,
                                                fontSize:
                                                  getRelativeFontSize(0),
                                              },
                                            },
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <TextField
                                      multiline
                                      rows={3}
                                      placeholder="Your Reply *"
                                      fullWidth
                                      value={replyContent}
                                      onChange={(e) =>
                                        setReplyContent(e.target.value)
                                      }
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          ...regularFont,
                                          fontSize: getRelativeFontSize(0),
                                          backgroundColor: alpha(
                                            theme.palette.background.default,
                                            0.8,
                                          ),
                                          "& fieldset": {
                                            borderColor: alpha(
                                              theme.palette.divider,
                                              0.2,
                                            ),
                                          },
                                          "&:hover fieldset": {
                                            borderColor: alpha("#f8481c", 0.4),
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#f8481c",
                                          },
                                        },
                                        "& .MuiInputBase-input": {
                                          ...regularFont,
                                          fontSize: getRelativeFontSize(0),
                                          "&::placeholder": {
                                            color: theme.palette.text.secondary,
                                            opacity: 0.7,
                                            ...regularFont,
                                            fontSize: getRelativeFontSize(0),
                                          },
                                        },
                                      }}
                                    />
                                    <Stack direction="row" spacing={2}>
                                      <Button
                                        onClick={() => {
                                          if (
                                            replyName &&
                                            replyEmail &&
                                            replyContent
                                          ) {
                                            handleSubmitReply(comment.id);
                                          }
                                        }}
                                        disabled={loading}
                                        sx={{
                                          ...boldFont,
                                          fontSize: getRelativeFontSize(1),
                                          px: 3,
                                          py: 1,
                                          background:
                                            "linear-gradient(135deg, #f8481c 0%, #e04119 100%)",
                                          color: "#ffffff",
                                          textTransform: "none",
                                          "&:hover": {
                                            background:
                                              "linear-gradient(135deg, #e04119 0%, #d03a16 100%)",
                                          },
                                        }}
                                      >
                                        Post Reply
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          setReplyingTo(null);
                                          setReplyName("");
                                          setReplyEmail("");
                                          setReplyContent("");
                                        }}
                                        sx={{
                                          ...mediumFont,
                                          fontSize: getRelativeFontSize(1),
                                          color: theme.palette.text.secondary,
                                          textTransform: "none",
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </Stack>
                                  </Stack>
                                </Box>
                              )}

                              {/* Replies */}
                              {comment.replies &&
                                comment.replies.length > 0 && (
                                  <Stack
                                    spacing={2}
                                    sx={{ mt: 2, ml: { xs: 0, md: 8 } }}
                                  >
                                    {comment.replies.map((reply: any) => (
                                      <Box
                                        key={reply.id}
                                        sx={{
                                          p: 2.5,
                                          pl: 3,
                                          background: alpha(
                                            theme.palette.background.paper,
                                            0.4,
                                          ),
                                          backdropFilter: "blur(10px)",
                                          borderRadius: "8px",
                                          border: `1px solid ${alpha(
                                            theme.palette.divider,
                                            0.1,
                                          )}`,
                                          borderLeft: `3px solid ${alpha(
                                            "#f8481c",
                                            0.5,
                                          )}`,
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          spacing={2}
                                          alignItems="center"
                                          mb={1}
                                        >
                                          <Typography
                                            sx={{
                                              ...mediumFont,
                                              fontSize: getRelativeFontSize(1),
                                              color: theme.palette.text.primary,
                                            }}
                                          >
                                            {reply.author_name}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              ...regularFont,
                                              fontSize: getRelativeFontSize(0),
                                              color:
                                                theme.palette.text.secondary,
                                            }}
                                          >
                                            {formatDate(reply.created_at)}
                                          </Typography>
                                        </Stack>
                                        <Typography
                                          sx={{
                                            ...regularFont,
                                            fontSize: getRelativeFontSize(1),
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.7,
                                          }}
                                        >
                                          {reply.body}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}
                            </Box>
                          ))}
                        </Stack>
                      )}
                      {commentsSectionExpanded &&
                        (!comments || comments.length === 0) && (
                          <Typography
                            sx={{
                              ...regularFont,
                              fontSize: getRelativeFontSize(2),
                              color: theme.palette.text.secondary,
                              py: 4,
                              fontStyle: "italic",
                            }}
                          >
                            No comments yet. Be the first to share your
                            thoughts.
                          </Typography>
                        )}
                    </Box>
                  </Collapse>
                </Box>
              </motion.div>

              {/* Leave a Reply Section - Collapsible */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
              >
                <Box
                  sx={{
                    my: 6,
                    pt: 5,
                    borderTop: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "-2px",
                      left: 0,
                      width: "100px",
                      height: "2px",
                      background:
                        "linear-gradient(90deg, #f8481c 0%, transparent 100%)",
                    },
                  }}
                >
                  <Box
                    onClick={() => setReplySectionExpanded((prev) => !prev)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      py: 1.5,
                      px: 2,
                      borderRadius: "10px",
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      background: replySectionExpanded
                        ? alpha(
                            "#f8481c",
                            theme.palette.mode === "dark" ? 0.15 : 0.08,
                          )
                        : alpha(theme.palette.background.paper, 0.9),
                      "&:hover": {
                        background: alpha(
                          "#f8481c",
                          theme.palette.mode === "dark" ? 0.2 : 0.12,
                        ),
                        borderColor: alpha("#f8481c", 0.4),
                      },
                      transition:
                        "background 0.2s ease, border-color 0.2s ease",
                    }}
                  >
                    <ChatBubbleOutlineIcon
                      sx={{ fontSize: "28px", color: "#f8481c", flexShrink: 0 }}
                    />
                    <Typography
                      component="h3"
                      sx={{
                        ...boldFont,
                        flex: 1,
                        fontSize: {
                          xs: getRelativeFontSize(12),
                          md: getRelativeFontSize(15),
                        },
                        color: theme.palette.text.primary,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Leave a Reply
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: "28px",
                        color: "#f8481c",
                        transform: replySectionExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.25s ease",
                      }}
                    />
                  </Box>

                  <Collapse in={replySectionExpanded}>
                    <Box sx={{ pt: 3 }}>
                      <Typography
                        sx={{
                          ...regularFont,
                          fontSize: getRelativeFontSize(2),
                          color: theme.palette.text.secondary,
                          mb: 3,
                        }}
                      >
                        Your email address will not be published. Required
                        fields are marked *
                      </Typography>

                      <Box
                        sx={{
                          mt: 4,
                          background: `linear-gradient(135deg, ${alpha(
                            theme.palette.background.paper,
                            0.9,
                          )} 0%, ${alpha("#f8481c", 0.03)} 100%)`,
                          backdropFilter: "blur(20px)",
                          borderRadius: "12px",
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          boxShadow: `0 8px 32px ${alpha(
                            theme.palette.mode === "dark"
                              ? "#000000"
                              : "#000000",
                            0.1,
                          )}`,
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "4px",
                            height: "100%",
                            background:
                              "linear-gradient(180deg, #f8481c 0%, #ff6b4a 100%)",
                            borderRadius: "12px 0 0 12px",
                            zIndex: 0,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: 1,
                            p: { xs: 3, md: 4 },
                          }}
                        >
                          <Stack spacing={3}>
                            {/* Comment Field */}
                            <TextField
                              multiline
                              rows={2}
                              placeholder="Comment *"
                              fullWidth
                              required
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: alpha(
                                    theme.palette.background.default,
                                    0.8,
                                  ),
                                  backdropFilter: "blur(10px)",
                                  borderRadius: "8px",
                                  "& fieldset": {
                                    borderColor: alpha(
                                      theme.palette.divider,
                                      0.2,
                                    ),
                                    borderWidth: "2px",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: alpha("#f8481c", 0.4),
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#f8481c",
                                    borderWidth: "2px",
                                    boxShadow: `0 0 0 4px ${alpha("#f8481c", 0.1)}`,
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  ...regularFont,
                                  color: theme.palette.text.primary,
                                  fontSize: getRelativeFontSize(0),
                                  padding: "14px !important",
                                  "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                    opacity: 0.7,
                                    ...regularFont,
                                    fontSize: getRelativeFontSize(0),
                                  },
                                },
                                "& .MuiInputBase-inputMultiline": {
                                  padding: "14px !important",
                                  ...(loading && {
                                    opacity: 0.5,
                                    cursor: "not-allowed",
                                  }),
                                },
                              }}
                              disabled={loading}
                              value={formFields?.comment ?? ""}
                              onChange={(e) =>
                                setFormFields({
                                  ...formFields,
                                  comment: e.target.value,
                                })
                              }
                            />

                            {/* Name, Email, Website Row */}
                            <Grid
                              container
                              spacing={2}
                              sx={{ margin: 0, width: "100%" }}
                            >
                              <Grid
                                item
                                xs={12}
                                sm={4}
                                sx={{
                                  paddingLeft: {
                                    xs: "0 !important",
                                    sm: "0 !important",
                                  },
                                }}
                              >
                                <TextField
                                  placeholder="Name *"
                                  fullWidth
                                  required
                                  disabled={loading || authenticated}
                                  value={formFields?.name ?? ""}
                                  onChange={(e) =>
                                    setFormFields({
                                      ...formFields,
                                      name: e.target.value,
                                    })
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: alpha(
                                        theme.palette.background.default,
                                        0.8,
                                      ),
                                      backdropFilter: "blur(10px)",
                                      borderRadius: "8px",
                                      "& fieldset": {
                                        borderColor: alpha(
                                          theme.palette.divider,
                                          0.2,
                                        ),
                                        borderWidth: "2px",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: alpha("#f8481c", 0.4),
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#f8481c",
                                        borderWidth: "2px",
                                        boxShadow: `0 0 0 4px ${alpha(
                                          "#f8481c",
                                          0.1,
                                        )}`,
                                      },
                                    },
                                    "& .MuiInputBase-input": {
                                      ...regularFont,
                                      color: theme.palette.text.primary,
                                      fontSize: getRelativeFontSize(0),
                                      padding: "14px",
                                      "&::placeholder": {
                                        color: theme.palette.text.secondary,
                                        opacity: 0.7,
                                        ...regularFont,
                                        fontSize: getRelativeFontSize(0),
                                        ...(loading && {
                                          opacity: 0.5,
                                          cursor: "not-allowed",
                                        }),
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={4}
                                sx={{
                                  paddingLeft: {
                                    xs: "0 !important",
                                    sm: "16px !important",
                                  },
                                }}
                              >
                                <TextField
                                  type="email"
                                  placeholder="Email *"
                                  fullWidth
                                  required
                                  disabled={loading || authenticated}
                                  value={formFields?.email ?? ""}
                                  onChange={(e) =>
                                    setFormFields({
                                      ...formFields,
                                      email: e.target.value,
                                    })
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: alpha(
                                        theme.palette.background.default,
                                        0.8,
                                      ),
                                      backdropFilter: "blur(10px)",
                                      borderRadius: "8px",
                                      "& fieldset": {
                                        borderColor: alpha(
                                          theme.palette.divider,
                                          0.2,
                                        ),
                                        borderWidth: "2px",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: alpha("#f8481c", 0.4),
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#f8481c",
                                        borderWidth: "2px",
                                        boxShadow: `0 0 0 4px ${alpha(
                                          "#f8481c",
                                          0.1,
                                        )}`,
                                      },
                                    },
                                    "& .MuiInputBase-input": {
                                      ...regularFont,
                                      color: theme.palette.text.primary,
                                      fontSize: getRelativeFontSize(0),
                                      padding: "14px",
                                      "&::placeholder": {
                                        color: theme.palette.text.secondary,
                                        opacity: 0.7,
                                        ...regularFont,
                                        fontSize: getRelativeFontSize(0),
                                        ...(loading && {
                                          opacity: 0.5,
                                          cursor: "not-allowed",
                                        }),
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={4}
                                sx={{
                                  paddingLeft: {
                                    xs: "0 !important",
                                    sm: "16px !important",
                                  },
                                }}
                              >
                                <TextField
                                  placeholder="Website"
                                  fullWidth
                                  disabled={loading}
                                  value={formFields?.website ?? ""}
                                  onChange={(e) =>
                                    setFormFields({
                                      ...formFields,
                                      website: e.target.value,
                                    })
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: alpha(
                                        theme.palette.background.default,
                                        0.8,
                                      ),
                                      backdropFilter: "blur(10px)",
                                      borderRadius: "8px",
                                      "& fieldset": {
                                        borderColor: alpha(
                                          theme.palette.divider,
                                          0.2,
                                        ),
                                        borderWidth: "2px",
                                      },
                                      "&:hover fieldset": {
                                        borderColor: alpha("#f8481c", 0.4),
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#f8481c",
                                        borderWidth: "2px",
                                        boxShadow: `0 0 0 4px ${alpha(
                                          "#f8481c",
                                          0.1,
                                        )}`,
                                      },
                                    },
                                    "& .MuiInputBase-input": {
                                      ...regularFont,
                                      color: theme.palette.text.primary,
                                      fontSize: getRelativeFontSize(0),
                                      padding: "14px",
                                      "&::placeholder": {
                                        color: theme.palette.text.secondary,
                                        opacity: 0.7,
                                        ...regularFont,
                                        fontSize: getRelativeFontSize(0),
                                      },
                                      ...(loading && {
                                        opacity: 0.5,
                                        cursor: "not-allowed",
                                      }),
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>

                            {/* Checkbox for saving info */}
                            {/* <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <input
                            type="checkbox"
                            id="save-info"
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                              accentColor: "#f8481c",
                            }}
                          />
                          <Typography
                            component="label"
                            htmlFor="save-info"
                            sx={{
                              ...regularFont,
                              fontSize: getRelativeFontSize(0),
                              color: theme.palette.text.secondary,
                              cursor: "pointer",
                            }}
                          >
                            Save my name, email, and website in this browser for
                            the next time I comment.
                          </Typography>
                        </Box> */}

                            {/* Submit Button */}
                            <Box>
                              <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Button
                                  onClick={() => handleSubmitComment()}
                                  variant="contained"
                                  sx={{
                                    px: 5,
                                    py: 1.8,
                                    borderRadius: "8px",
                                    background:
                                      "linear-gradient(135deg, #f8481c 0%, #e04119 100%)",
                                    color: "#ffffff",
                                    ...boldFont,
                                    fontSize: getRelativeFontSize(1),
                                    textTransform: "none",
                                    boxShadow: `0 4px 16px ${alpha(
                                      "#f8481c",
                                      0.3,
                                    )}`,
                                    position: "relative",
                                    overflow: "hidden",
                                    "&::before": {
                                      content: '""',
                                      position: "absolute",
                                      top: 0,
                                      left: "-100%",
                                      width: "100%",
                                      height: "100%",
                                      background:
                                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                                      transition: "left 0.5s ease",
                                    },
                                    "&:hover": {
                                      background:
                                        "linear-gradient(135deg, #e04119 0%, #d03a16 100%)",
                                      boxShadow: `0 6px 24px ${alpha(
                                        "#f8481c",
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                      "&::before": {
                                        left: "100%",
                                      },
                                    },
                                    transition: "all 0.3s ease",
                                    ...(loading && {
                                      opacity: 0.5,
                                      cursor: "not-allowed",
                                    }),
                                  }}
                                >
                                  Post
                                </Button>
                              </motion.div>
                            </Box>
                          </Stack>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              </motion.div>
            </Grid>

            {/* Right Column - Sidebar */}
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Box
                  sx={{
                    position: "sticky",
                    top: 100,
                  }}
                >
                  {/* Similar Blogs Section */}
                  {/* <Box
                    sx={{
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.8
                      ),
                      backdropFilter: "blur(10px)",
                      borderRadius: "8px",
                      p: 4,
                      mb: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: `0 4px 24px ${alpha(
                        theme.palette.mode === "dark" ? "#000000" : "#000000",
                        0.1
                      )}`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "4px",
                        height: "100%",
                        background:
                          "linear-gradient(180deg, #f8481c 0%, #ff6b4a 100%)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        ...boldFont,
                        fontSize: getRelativeFontSize(3),
                        color: theme.palette.text.primary,
                        mb: 3,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <ArticleIcon
                        sx={{ fontSize: "22px", color: "#f8481c" }}
                      />
                      Similar Blogs
                    </Typography>
                    <Stack spacing={2.5}>
                      {similarBlogs.map((similarBlog, index) => (
                        <motion.div
                          key={similarBlog.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <Box
                            onClick={() =>
                              history.push(`/blog/${similarBlog.id}`)
                            }
                            sx={{
                              display: "flex",
                              gap: 2,
                              cursor: "pointer",
                              p: 1.5,
                              borderRadius: "8px",
                              backgroundColor: alpha(
                                theme.palette.background.default,
                                0.5
                              ),
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.1
                              )}`,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: alpha("#f8481c", 0.08),
                                border: `1px solid ${alpha("#f8481c", 0.3)}`,
                                transform: "translateX(4px)",
                                boxShadow: `0 4px 12px ${alpha(
                                  "#f8481c",
                                  0.15
                                )}`,
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={similarBlog.image}
                              alt={similarBlog.title}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (target && FALLBACK_IMAGE)
                                  target.src = FALLBACK_IMAGE;
                              }}
                              sx={{
                                width: "100px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                flexShrink: 0,
                              }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  ...mediumFont,
                                  fontSize: getRelativeFontSize(0),
                                  color: theme.palette.text.primary,
                                  mb: 0.5,
                                  lineHeight: 1.3,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {similarBlog.title}
                              </Typography>
                              <Typography
                                sx={{
                                  ...regularFont,
                                  fontSize: getRelativeFontSize(0),
                                  color: theme.palette.text.secondary,
                                  mt: 0.5,
                                }}
                              >
                                {formatDate(similarBlog.publishDate)}
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      ))}
                    </Stack>
                  </Box> */}

                  {/* Top Recommendations Section */}
                  {/* <Box
                    sx={{
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.8
                      ),
                      backdropFilter: "blur(10px)",
                      borderRadius: "8px",
                      p: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: `0 4px 24px ${alpha(
                        theme.palette.mode === "dark" ? "#000000" : "#000000",
                        0.1
                      )}`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "4px",
                        height: "100%",
                        background:
                          "linear-gradient(180deg, #f8481c 0%, #ff6b4a 100%)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        mb: 3,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <TrendingUpIcon
                        sx={{ fontSize: "20px", color: "#f8481c" }}
                      />
                      Top Recommendations
                    </Typography>
                    <Stack spacing={2.5}>
                      {topRecommendations.map((recBlog, index) => (
                        <motion.div
                          key={recBlog.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <Box
                            onClick={() => history.push(`/blog/${recBlog.id}`)}
                            sx={{
                              display: "flex",
                              gap: 2,
                              cursor: "pointer",
                              p: 1.5,
                              borderRadius: "8px",
                              backgroundColor: alpha(
                                theme.palette.background.default,
                                0.5
                              ),
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.1
                              )}`,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: alpha("#f8481c", 0.08),
                                border: `1px solid ${alpha("#f8481c", 0.3)}`,
                                transform: "translateX(4px)",
                                boxShadow: `0 4px 12px ${alpha(
                                  "#f8481c",
                                  0.15
                                )}`,
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={recBlog.image}
                              alt={recBlog.title}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (target && FALLBACK_IMAGE)
                                  target.src = FALLBACK_IMAGE;
                              }}
                              sx={{
                                width: "100px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                flexShrink: 0,
                              }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  ...mediumFont,
                                  fontSize: getRelativeFontSize(0),
                                  color: theme.palette.text.primary,
                                  mb: 0.5,
                                  lineHeight: 1.3,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {recBlog.title}
                              </Typography>
                              <Typography
                                sx={{
                                  ...regularFont,
                                  fontSize: getRelativeFontSize(0),
                                  color: theme.palette.text.secondary,
                                  mt: 0.5,
                                }}
                              >
                                {formatDate(recBlog.publishDate)}
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      ))}
                    </Stack>
                  </Box> */}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default BlogDetail;
